// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';

/** ====== Types ====== */
export interface UserPermissionDTO {
  moduleId: string;
  moduleName: string;
  moduleDescription: string;

  // Core
  canCreate: boolean;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canReport: boolean;

  // Extended actions
  canAssign: boolean;
  canComment: boolean;
  canUpload: boolean;
  canApprove: boolean;
  canReject: boolean;
  canChangeStatus: boolean;
  canArchive: boolean;
  canRestore: boolean;
  canExport: boolean;
  canImport: boolean;
  canShare: boolean;

  // UI visibility flags
  visibleInSidebar: boolean;
  visibleInDashboard: boolean;
}

export type PermissionAction =
  | 'create' | 'view' | 'edit' | 'delete' | 'report'
  | 'assign' | 'comment' | 'upload' | 'approve' | 'reject'
  | 'changeStatus' | 'archive' | 'restore' | 'export' | 'import' | 'share';

interface User {
  name?: string;
  modules?: string[];
  role?: string;
  email?: string;
  profileImage?: string;
  mobile?: string;
  id?: string;
  tenantId?: string;
  tenantName?: string;
  username?: string;
  designation?: string;
  permissions?: UserPermissionDTO[];
}

interface LoginPayload {
  accessToken: string;
  refreshToken: string;
  user: User;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (payload: LoginPayload) => void;
  logout: () => void;

  /** Helper: check if user has a permission on a module. */
  hasPermission: (
    moduleName: string,
    actions: PermissionAction | PermissionAction[],
    mode?: 'any' | 'all'
  ) => boolean;
}

/** ====== Defaults / helpers ====== */
const DEFAULT_PERM: UserPermissionDTO = {
  moduleId: '',
  moduleName: '',
  moduleDescription: '',

  canCreate: false,
  canView: false,
  canEdit: false,
  canDelete: false,
  canReport: false,

  canAssign: false,
  canComment: false,
  canUpload: false,
  canApprove: false,
  canReject: false,
  canChangeStatus: false,
  canArchive: false,
  canRestore: false,
  canExport: false,
  canImport: false,
  canShare: false,

  visibleInSidebar: false,
  visibleInDashboard: false,
};

const PERM_FIELD_BY_ACTION: Record<PermissionAction, keyof UserPermissionDTO> = {
  create: 'canCreate',
  view: 'canView',
  edit: 'canEdit',
  delete: 'canDelete',
  report: 'canReport',
  assign: 'canAssign',
  comment: 'canComment',
  upload: 'canUpload',
  approve: 'canApprove',
  reject: 'canReject',
  changeStatus: 'canChangeStatus',
  archive: 'canArchive',
  restore: 'canRestore',
  export: 'canExport',
  import: 'canImport',
  share: 'canShare',
};

function normalizePermissionRow(row: Partial<UserPermissionDTO>): UserPermissionDTO {
  // Merge with defaults to ensure all flags exist (esp. after BE deploys)
  return {
    ...DEFAULT_PERM,
    ...row,
    // ensure booleans are truly boolean
    canCreate: !!row.canCreate,
    canView: !!row.canView,
    canEdit: !!row.canEdit,
    canDelete: !!row.canDelete,
    canReport: !!row.canReport,

    canAssign: !!row.canAssign,
    canComment: !!row.canComment,
    canUpload: !!row.canUpload,
    canApprove: !!row.canApprove,
    canReject: !!row.canReject,
    canChangeStatus: !!row.canChangeStatus,
    canArchive: !!row.canArchive,
    canRestore: !!row.canRestore,
    canExport: !!row.canExport,
    canImport: !!row.canImport,
    canShare: !!row.canShare,

    visibleInSidebar: !!row.visibleInSidebar,
    visibleInDashboard: !!row.visibleInDashboard,
  };
}

function normalizeUser(u: any): User {
  const perms = Array.isArray(u?.permissions) ? u.permissions.map(normalizePermissionRow) : [];
  return { ...u, permissions: perms };
}

/** ====== Context ====== */
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
  hasPermission: () => false,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const parsed = JSON.parse(raw);
        setUser(normalizeUser(parsed));
      }
    } catch {
      // ignore parse errors; user remains null
    }
  }, []);

  const hasPermission = (
    moduleName: string,
    actions: PermissionAction | PermissionAction[],
    mode: 'any' | 'all' = 'any'
  ) => {
    if (!user?.permissions || !moduleName) return false;
    const perm = user.permissions.find(
      p => p.moduleName?.toLowerCase() === moduleName.toLowerCase()
    );
    if (!perm) return false;

    const actionList = Array.isArray(actions) ? actions : [actions];
    const checks = actionList.map(a => !!perm[PERM_FIELD_BY_ACTION[a]]);

    return mode === 'all' ? checks.every(Boolean) : checks.some(Boolean);
  };

  const login = ({ accessToken, refreshToken, user }: LoginPayload) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(normalizeUser(user));
    window.location.href = '/';
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    localStorage.clear();
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, login, logout, hasPermission }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);